/**
 * For working with singular Page records.
 */

import {fromJS, List, Map, Record as ImmRecord} from 'immutable';
import {propIs} from '@packages/systems/core/utils/ImmUtils';
import {add, test} from '@packages/utilities/fp/utils';
import PageNode, {
  PageState,
  StaticPagesType,
  type PageRecordType,
  type PageListNodeType,
  PageKindType,
  ComponentPageKind,
} from '@packages/systems/core/records/PageRecord';
import {sortBy, startsWith, add as binaryAdd} from 'lodash';
import {memoize, weakMemo, singleMemo} from '@packages/utilities/memo';
import {
  makeUniqueSlug,
  getStaticPages,
  newSiblingIndex,
  fullSlug,
  getPage,
  getPageNodes,
  siblingIndexFromTarget,
  getDynPages,
} from './PageTreeUtils';
import {
  getId,
  uniqueNameCopy,
  uniqueSlugCopy,
  uniqueName,
  makeMintId,
} from '@packages/systems/core/utils/RecordUtils';
import {getElse, isIndexable} from '@packages/utilities/data-utils';
import {
  getTokenUsages,
  isItemRefTokenPath,
  getBindingContextTokenOptions,
} from '@packages/systems/core/utils/EmbedUtils/all';
import {getItemLabel} from './ItemUtils';
import type {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {dynamoPageSlugPrefix} from '@packages/systems/core/constants/SharedConfig';
import {webflowDefaultHost} from '@packages/utilities/environment';
import {reportBug} from '@packages/utilities/reporter';
import {
  isCategoriesCollection,
  isProductsCollection,
  isSkusCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import type {
  CollectionsType,
  ScheduleItemsType,
} from '@packages/systems/core/records/CollectionStateType';
import type {EmbedTokenOption} from '@packages/systems/core/utils/EmbedUtils/all';
import {getPageBindingContext} from '@packages/systems/dynamo/gateway';
import type {
  Locale,
  LocaleSubdirectory,
} from '@packages/systems/localization-types';
import type {SiteDataStateType} from '@packages/systems/designer/site-data';
import {getCollectionSlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {hashCode} from '@packages/utilities/string-utils';
import {
  PageDefaultIcon,
  PageTemplateIcon,
  BranchDefaultIcon,
  PageDraftIcon,
} from '@packages/systems/spring';
import {getSlugProps} from './getSlugProps';
import {ReviewStatusType} from '@packages/shared/types/reviews';
import {isReviewPending, isReviewApproved} from './ReviewUtils';
import {designerTranslation} from '@packages/systems/designer-translations';

/*::

// HACK: We import all of our store state types inside a Flow comment block to
// prevent Webpack from treating these as *hard* dependencies.
//
// Without this, Webpack can run into dependency cycle issues at runtime, even
// though these types are completely gone at runtime!
//
// These cycles are still a problem, and the *complete* fix is to separate all
// our store state types into separate files and import from those here (and
// anywhere that needs the types).
//
// A side-quest fix would be to figure out how to make sure Webpack ignores any
// `type` dependencies.
//
// Funny note: You could also use `import type { ... }` syntax to get around the
// webpack issue. I chose the comment syntax since it's more obvious what's
// going on.
import type {PageState, StaticPagesType} from '@packages/systems/core/records/PageRecord';
import type {SiteDataStateType} from '@packages/systems/designer/site-data';

*/

export const goToExternalActionUrl = (externalUrl: string) => {
  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Location | (string & Location)'.
  window.location = `${webflowDefaultHost}${externalUrl}`;
};

const DYNAMIC_PAGE_SETTING_PATHS = List([
  ['seoTitle'],
  ['seoDesc'],
  ['ogTitle'],
  ['ogDesc'],
  ['ogImages', 0],
  ['rssItemTitle'],
  ['rssItemDesc'],
  ['rssItemImage'],
  ['head'],
  ['postBody'],
]);

const ENTERPRISE_SNIPPETS = {
  Marketo: '%(marketo.(com|net)|munchkin|mkt(o|forms))%',
};

export const PAGE_SWITCHER_MENU_WIDTH = 320;
///
// Constructors and type conversions.
///

export const mintedPageDefaultProps = {
  name: 'Untitled',
  seoTitle: 'Untitled',
  ogTitleCopied: true,
  ogDescCopied: true,
  type: 'Page',
  rssItemTitleCopied: true,
  rssItemDescCopied: true,
  searchExclude: false,
  searchTitleCopied: true,
  searchDescriptionCopied: true,
  searchImageCopied: true,
  isStandaloneBranch: false,
  isStaticTemplate: false,
};

/**
 * Creates a new Page with default attributes nested under the provided
 * `parentId`.
 *
 * @param  {String} parentId
 * @param  {Immutable.Map.<String, PageNode>} pageTreeNodes
 * @return {PageNode}
 */
export const mintPage = (
  parentId: string,
  pageTreeNodes: Map<string, PageRecordType>
): PageRecordType => {
  return PageNode({
    ...mintedPageDefaultProps,
    id: makeMintId(),
    parentId,
    slug: makeUniqueSlug('untitled', parentId, pageTreeNodes),
    siblingIndex: newSiblingIndex(parentId, pageTreeNodes),
  });
};

/**
 * Creates a copy of the provided `page`. The new page will have its slug and
 * name deduped by appending the word `copy`.
 *
 * @param  {PageNode} page
 * @param  {Immutable.Map.<String, PageNode>} pageTreeNodes
 * @return {PageNode}
 */
export const duplicatePage = (
  page: PageRecordType,
  pageTreeNodes: Map<string, PageRecordType>,
  overrides = {}
): PageRecordType => {
  const slug = isHomePage(page) ? 'home' : page.slug;

  let duplicatedPage = PageNode(fromJS(page)).merge({
    ...page.toJS(),
    isHome: false,
    id: makeMintId(),
    duplicateOf: page.id,
    name: uniqueNameCopy(page.name, pageTreeNodes),
    slug: uniqueSlugCopy(slug, pageTreeNodes),
    siblingIndex: siblingIndexFromTarget(page, pageTreeNodes, true),
    type: 'Page',
    ...overrides,
  });

  duplicatedPage
    .get('localizedSettings')
    ?.forEach((settingsForLocale, localeId) => {
      const localizedSlug = settingsForLocale.get('slug');
      if (localizedSlug != null) {
        duplicatedPage = duplicatedPage.setIn(
          ['localizedSettings', localeId, 'slug'],
          uniqueSlugCopy(localizedSlug, pageTreeNodes, {localeId})
        );
      }
    });

  return duplicatedPage;
};

/**
 * Creates a new Dynamic Page representing the provided `collection`.
 *
 * @param  {Collection} collection
 * @return {PageNode}
 */
export const mintDynamicPage = (
  collection: CollectionRecordType
): PageRecordType =>
  PageNode({
    id: makeMintId(),
    name: `${collection.name} Template`,

    slug: `detail_${collection.slug}`,
    seoTitle: '{{name}}',
    ogTitleCopied: true,
    ogDescCopied: true,
    dynCollection: collection._id,
    type: 'Page',
    rssItemTitleCopied: true,
    rssItemDescCopied: true,
    searchExclude: false,
    searchTitleCopied: true,
    searchImageCopied: true,
  });

/**
 * Demotes the provided home page to a regular page.
 *
 * @param  {PageNode} page
 * @param  {Immutable.Map.<String, PageNode>} pageTreeNodes
 * @return {PageNode}
 */
export const fromHomePage = (
  page: PageRecordType,
  pageTreeNodes: Map<string, PageRecordType>
): PageRecordType =>
  page.merge({
    name: uniqueName('Old Home', pageTreeNodes),
    slug: makeUniqueSlug('old-home', page.parentId, pageTreeNodes),
    isHome: false,
  });

///
// Predicates.
///

export const isFolder = (node: any) =>
  isIndexable(node) && getElse('type', '', node) === 'Folder';

export const isPage = (node: any) =>
  isIndexable(node) && getElse('type', '', node).toLowerCase() === 'page';

export const isSiteBranchPage = (
  page: PageRecordType | PageListNodeType
): boolean =>
  isPage(page) && Boolean(page.branchId) && Boolean(page.branchRole);

export const isBranchPage = (
  page: PageRecordType | PageListNodeType
): boolean => isPage(page) && Boolean(page.branchId) && !page.branchRole;

/** Legacy page branch or site-branch working/base page. */
export const isBranchOrSiteBranchPage = (
  page: PageRecordType | PageListNodeType
): boolean => isBranchPage(page) || isSiteBranchPage(page);

export const isStandaloneBranchPage = (
  page: PageRecordType | PageListNodeType
) => isPage(page) && isBranchPage(page) && page.isStandaloneBranch;

export const isStandaloneBranchParentPage = (
  page: PageRecordType,
  {
    currentLocale,
    canDraftPagesPerLocale,
  }: {currentLocale: Locale | null | undefined; canDraftPagesPerLocale: boolean}
) =>
  isPage(page) &&
  isDraftPage(page, {currentLocale, canDraftPagesPerLocale}) &&
  page.isStandaloneBranch;

export const getPageDisplayName = ({
  page,
  currentItem,
  currentLocale,
  showPrimaryName,
}: {
  page: PageRecordType;
  currentItem?: ItemRecordType | null;
  currentLocale?: Locale | null;
  showPrimaryName?: boolean;
}) => {
  const pageName =
    showPrimaryName || !currentLocale || currentLocale.primary
      ? page.name
      : (page.localizedSettings?.getIn([currentLocale._id, 'title']) ??
        page.name);
  if (isStaticTemplatePage(page)) {
    return `${pageName} Template`;
  } else if (isDynPage(page)) {
    return currentItem ? currentItem.get('name') : `${pageName} [No items]`;
  } else {
    return pageName;
  }
};

export const isStaticTemplatePage = (page: PageRecordType | PageListNodeType) =>
  isPage(page) && page.isStaticTemplate;

export type PageType =
  | 'branch'
  | 'static-template'
  | 'draft'
  | 'normal'
  | 'collection';

/**
 * Returns the type of page. Used within the Finder to avoid passing bool flags.
 * NOTE: Draft pages in different locales will be displayed as normal pages. That is intended behavior.
 */
export const getPageType = (
  page: PageRecordType | PageListNodeType | undefined,
  {currentLocale, canDraftPagesPerLocale}: Parameters<typeof isDraftPage>[1]
): PageType => {
  if (!page) {
    return 'normal';
  }

  if (isBranchPage(page)) {
    return 'branch';
  } else if (isStaticTemplatePage(page)) {
    return 'static-template';
  } else if (isDraftPage(page, {currentLocale, canDraftPagesPerLocale})) {
    return 'draft';
  } else if (page.dynCollection !== null) {
    return 'collection';
  }
  return 'normal';
};

export const getPageColorForType = (
  pageType: PageType
): 'purple' | 'orange' | 'secondary' => {
  switch (pageType) {
    case 'collection':
      return 'purple';
    case 'branch':
    case 'draft':
      return 'orange';
    case 'static-template':
    default:
      return 'secondary';
  }
};

export const getPageIconForType = (pageType: PageType) => {
  switch (pageType) {
    case 'static-template':
      return PageTemplateIcon;
    case 'branch':
      return BranchDefaultIcon;
    case 'draft':
      return PageDraftIcon;
    case 'collection':
    case 'normal':
    default:
      return PageDefaultIcon;
  }
};

// Fragile check to determine if page has a been branched (does not verify page type or parentId on branch page)
export const isBasePage = (page: PageRecordType | PageListNodeType) =>
  isPage(page) &&
  !isBranchPage(page) &&
  !page.canBranch &&
  !isSiteBranchPage(page);

export const isHomePage = (page: any) => isPage(page) && page.isHome;

export const isDynPage = (node: any) =>
  isPage(node) && getElse('dynCollection', null, node) !== null;

export const isDraftPage = (
  node: any,
  {
    currentLocale,
    canDraftPagesPerLocale,
  }: {currentLocale: Locale | null | undefined; canDraftPagesPerLocale: boolean}
) => {
  if (!isPage(node)) {
    return false;
  }

  if (!currentLocale || !canDraftPagesPerLocale) {
    return node.get('draft', false);
  }

  const localizedSettings: {draft: boolean}[] = Object.values(
    node.localizedSettings?.toJS?.() ?? {}
  );
  const isAnyLocaleDraftStatusSet = localizedSettings.some(
    (localizedSetting) => typeof localizedSetting?.draft === 'boolean'
  );
  if (isAnyLocaleDraftStatusSet) {
    return node.getIn(['localizedSettings', currentLocale._id, 'draft'], false);
  }
  return node.get('draft', false);
};

/**
 * Custom type guard to check if a `PageKindType` is a ComponentPageKind.
 */
export const isComponentPageKind = (
  value: PageKindType | null | undefined
): value is ComponentPageKind => value?.kind === 'component';

/**
 * Determines if a given page represents a Component Page.
 */
export const isComponentPage = (page: PageRecordType | undefined) =>
  Boolean(page) &&
  isPage(page) &&
  isComponentPageKind(getElse('pageKind', null, page));

export const isGroupAccessOnly = (node: any, isUsersDisabled: boolean) => {
  return (
    !isUsersDisabled &&
    (isPage(node) || isFolder(node)) &&
    getElse('groupAccess', List(), node).size > 0
  );
};

export const isCheckoutConfirmationPage = (node: any) => {
  if (node.slug === 'order-confirmation') {
    return true;
  }
  return false;
};

export const isCheckoutPage = (node: any) => {
  if (node.slug === 'checkout') {
    return true;
  }
  return false;
};

export const isPayPalCheckoutPage = (node: any) => {
  if (node.slug === 'paypal-checkout') {
    return true;
  }
  return false;
};

export const isStaticPage = (node: any) =>
  isPage(node) &&
  getElse('dynCollection', null, node) === null &&
  !isComponentPage(node);

export const isUtilityPage = (node: any) => {
  if (!isPage(node)) {
    return false;
  }
  if (node.utilKey) {
    return true;
  }
  return false;
};

export const isStaticUtilityPage = (node: any) => {
  return isUtilityPage(node) && (node.slug === '401' || node.slug === '404');
};

export const isPageIdStaticUtilityPage =
  (pageState: PageState) => (pageId: string) =>
    isStaticUtilityPage(getPage(pageId, pageState));

export const isPublishedPage = (
  page: PageRecordType,
  siteLastPublishedOn: string
) => page && page.createdOn <= siteLastPublishedOn;

// No "beta" pages for now
export const isBetaPage = () => false;

export const isUserSystemsPage = (node: any) => {
  return isUtilityPage(node) && node.utilKey.includes('usys-');
};

export const isEcommercePage = (
  node: PageRecordType,
  collections: CollectionsType
): boolean => {
  if (!isPage(node) && !node.dynCollection && !collections) {
    return false;
  }
  const pageCollection =
    node.dynCollection && collections.get(node.dynCollection);
  return pageCollection
    ? isProductsCollection(pageCollection) ||
        isCategoriesCollection(pageCollection) ||
        isSkusCollection(pageCollection)
    : false;
};

///
// Miscellaneous.
//
// TODO(rocco): These aren't technically Page utilities per se, need to decide
// where to put them.
///

export const hasCreatedPages = (pageState: PageState) =>
  getStaticPages(pageState).count() > 1;

/**
 * Whether a page counts toward the site MAX_PAGES population.
 * Excludes utilities, static page templates, and site-branch working/base rows.
 * Legacy page-branch rows (branchId set, no branchRole) stay included.
 */
export const countsTowardStaticPageLimit = (page: PageRecordType): boolean =>
  !isUtilityPage(page) && !isStaticTemplatePage(page) && !page.branchRole;

/**
 * Whether a page counts toward MAX_PAGE_TEMPLATES.
 * Excludes site-branch working/base template copies; legacy page-branch
 * templates (branchId set, no branchRole) stay included.
 */
export const countsTowardStaticPageTemplateLimit = (
  page: PageRecordType
): boolean => isStaticTemplatePage(page) && !page.branchRole;

/**
 * Frontend equivalent of site.getStaticPageCount.
 * Returns the count of static pages, omitting detail pages, folders, utility pages,
 * static page templates, and site-branch working/base rows. Legacy page-branch
 * rows (branchId set, no branchRole) stay counted so Designer paywalls match
 * the server MAX_PAGES meter.
 *
 * Memoized with singleMemo (size-1 cache) to avoid redundant computation during
 * Flux update cycles when called multiple times with the same PageState.
 */
export const getStaticPageCount = singleMemo((pageState: PageState): number => {
  return getStaticPages(pageState).filter(countsTowardStaticPageLimit).count();
});

export const remainingPages = (
  siteData: SiteDataStateType,
  pageState: PageState
) => siteData.maxPages - getStaticPageCount(pageState);

/**
 * Returns the count of static page templates.
 *
 * Memoized with singleMemo (size-1 cache) to avoid redundant computation during
 * Flux update cycles when called multiple times with the same PageState.
 */
export const getStaticPageTemplateCount = singleMemo(
  (pageState: PageState): number => {
    return getStaticPages(pageState)
      .filter(countsTowardStaticPageTemplateLimit)
      .count();
  }
);

/**
 * Builds a sorted object array of static and dynamic pages from data already queried by the designer/editor.
 * @return {Array} Array of collections, each with items representing pages. The first is a pseudo collection
 * containing all the static pages.
 */
export const getPagesAsGroupedArray = function ({
  staticPages,
  collections = Map(),
  items = List(),
  scheduledItems,
  currentLocale,
  canDraftPagesPerLocale,
  skipBranchPages = false,
}: {
  staticPages: StaticPagesType;
  collections?: CollectionsType;
  items?: List<ItemRecordType>;
  scheduledItems: ScheduleItemsType;
  currentLocale: Locale | null | undefined;
  canDraftPagesPerLocale: boolean;
  skipBranchPages?: boolean;
}) {
  return _getPagesAsGroupedArray(items)(collections)(scheduledItems)(
    staticPages
  )(currentLocale)(canDraftPagesPerLocale)(skipBranchPages);
};

const _getPagesAsGroupedArray = weakMemo((items: List<ItemRecordType>) =>
  weakMemo((collections: CollectionsType) =>
    weakMemo((scheduledItems: ScheduleItemsType) =>
      weakMemo((staticPages: StaticPagesType) =>
        memoize((currentLocale: Locale | null | undefined) =>
          memoize((canDraftPagesPerLocale: boolean) =>
            memoize((skipBranchPages: boolean) => {
              // Build Static Group
              const staticPageGroup = {
                uuid: 'static_group',
                label: 'MAIN PAGES',
                slug: '',
                items: [],
              } as const;

              const getLocalizedSlug = getLocalizedSlugFactory(currentLocale);

              const localeSubdirectory = currentLocale
                ? getLocaleSubdirectory(currentLocale)
                : '';

              // Static pages
              staticPages.forEach((page, pageId) => {
                if (
                  pageId !== '__ROOT__' &&
                  page.type !== 'Folder' &&
                  !isUtilityPage(page)
                ) {
                  if (skipBranchPages && isBranchPage(page)) {
                    return;
                  }

                  let prefix = '/';

                  const parentId = page.parentId;

                  if (parentId) {
                    for (
                      let parent = staticPages.get(parentId);
                      parent && parent.slug;
                      // @ts-expect-error - TS2339 - No overload matches this call.
                      parent = staticPages.get(parent.parentId)
                    ) {
                      const parentSlug = getLocalizedSlug(parent);

                      prefix = '/' + parentSlug + prefix;
                    }
                  }

                  // @ts-expect-error - TS2339 - Property 'title' does not exist on type 'PageRecordType'.
                  const pageName = page.name || page.title;

                  const pageSlug = getLocalizedSlug(page);

                  const isPageWithoutSlug = page.isHome || pageSlug === null;

                  // @ts-expect-error - TS2339 - Property 'push' does not exist on type 'readonly []'.
                  staticPageGroup.items.push({
                    uuid: 'static_group' + pageId,
                    label: isDraftPage(page, {
                      currentLocale,
                      canDraftPagesPerLocale,
                    })
                      ? '[Draft] ' + pageName
                      : pageName,
                    value:
                      !localeSubdirectory && isPageWithoutSlug
                        ? '/'
                        : (localeSubdirectory ? '/' + localeSubdirectory : '') +
                          (isPageWithoutSlug ? '' : prefix + pageSlug),

                    pageId,
                    normalizedValue: pageName.toLowerCase(),
                  });
                }
              });

              // Build Dynamic Page Groups
              const dynamicGroups: Record<string, any> = {};
              if (collections && items) {
                // Collection Groups
                collections.forEach((collection, collectionId) => {
                  // SKUs items do not have dynamic pages, they all go under products
                  if (!isSkusCollection(collection)) {
                    dynamicGroups[collectionId] = {
                      uuid: collectionId, // unique group id used as a key
                      label: collection.get('name').toUpperCase(),
                      slug: getCollectionSlug(
                        collection.toJS(),
                        currentLocale?.cmsId
                      ),
                      items: [],
                    };
                  }
                });

                // Dynamic Pages
                items.forEach((dynItem) => {
                  const dynItemId = dynItem.get('_id');
                  const collectionId = dynItem.get('_cid');
                  const dynamicGroup = dynamicGroups[collectionId];
                  // skips over newly 'minted' unsaved items
                  if (dynamicGroup) {
                    dynamicGroup.items.push({
                      uuid: collectionId + dynItemId,
                      itemId: dynItemId,
                      label: getItemLabel(dynItem, scheduledItems),
                      value:
                        '/' + dynamicGroup.slug + '/' + dynItem.get('slug'),
                      collectionId,
                      normalizedValue: (
                        dynamicGroup.label +
                        ' ' +
                        dynItem.get('name')
                      ).toLowerCase(),
                    });
                  }
                });
              }
              // Build Master Page Group Array
              const dynPages: Array<any> = [];
              for (const collectionId in dynamicGroups) {
                dynPages.push(dynamicGroups[collectionId]);
              }

              // Sort dyn page groups after the static page group
              const pages = [staticPageGroup].concat(
                sortBy(dynPages, pageSort)
              );

              // Sort the pages in each group
              for (let i = 0; i < pages.length; i++) {
                const pageGroup = pages[i];
                // @ts-expect-error - TS2540 - Cannot assign to 'items' because it is a read-only property.
                pageGroup.items = sortBy(pageGroup.items, pageSort);
              }

              function pageSort(p: {label: string}): string {
                return p.label;
              }

              return pages;
            })
          )
        )
      )
    )
  )
);

/**
 * Returns the subdirectory of a locale to be used for example
 * in a process of constructing a full href of a page.
 */
function getLocaleSubdirectory(
  locale: Locale | null | undefined
): LocaleSubdirectory {
  if (!locale) {
    return '';
  }
  if (locale.primary && !locale.enabled) {
    return '';
  }
  return (locale.subdirectory || locale.tag).toLowerCase();
}

function getLocalizedSlugFactory(
  locale: Locale | null | undefined
): (page: PageRecordType) => string {
  if (locale && !locale.primary) {
    const localeId = locale._id;
    return (page) =>
      page.localizedSettings?.getIn([localeId, 'slug']) ?? page.slug;
  }

  return (page) => page.slug;
}

export const getPageSettingsBindings = (
  pages: any,
  collections: CollectionsType
) => {
  // @ts-expect-error - TS7006 - Parameter 'pageSettingsBindingsMap' implicitly has an 'any' type. | TS7006 - Parameter 'page' implicitly has an 'any' type.
  return pages.reduce((pageSettingsBindingsMap, page) => {
    const collection = collections.get(page.get('dynCollection'));
    if (!collection) {
      // If the type is invalid, report the bug, and return a Null Object;
      reportBug(
        'CollectionNotFound',
        'Collection bound to page was not found',
        {team: 'dynamo', ticket: 'https://app.clickup.com/t/2310520/DYN-605'}
      );
      return pageSettingsBindingsMap;
    }
    const field = collection.fields.find(
      propIs('slug', page.get('rssItemPubDateSlug'))
    );
    const rssItemPubDateUsages = fromJS({
      [getId(collection)]: {
        [getId(field)]: 1,
      },
    });
    return DYNAMIC_PAGE_SETTING_PATHS.reduce(
      (bindingsMap, path) => {
        const currentCollection = collections.get(page.get('dynCollection'));
        const tokenOptions = getTokenOptions(page, collections);
        const tokenUsages = tokenOptions
          ? getTokenUsages(page.getIn(path) || '', tokenOptions)
          : Map();
        const bindings = tokenUsages.reduce((map, count, tokenPath) => {
          const isItemRefToken = isItemRefTokenPath(tokenPath);
          if (isItemRefToken) {
            // @ts-expect-error - TS18046 - 'tokenPath' is of type 'unknown'.
            const [itemRefPart, slugPart] = tokenPath.split(':');
            const itemRefField = currentCollection

              .get('fields')
              .find(propIs('slug', itemRefPart));
            const itemRefCollectionId =
              itemRefField &&
              itemRefField.getIn(['validations', 'collectionId']);
            const itemRefValueCollection =
              // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'string'.
              itemRefCollectionId && collections.get(itemRefCollectionId);
            const itemRefValueField =
              itemRefValueCollection &&
              itemRefValueCollection
                // @ts-expect-error - TS2339 - Property 'get' does not exist on type '{}'.
                .get('fields')
                .find(propIs('slug', slugPart));
            const itemRefValueFieldId =
              itemRefValueField && getId(itemRefValueField);
            return itemRefCollectionId && itemRefValueFieldId
              ? map
                  .updateIn(
                    [getId(currentCollection), getId(itemRefField)],
                    0,
                    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number'.
                    add(count)
                  )
                  .updateIn(
                    [itemRefCollectionId, itemRefValueFieldId],
                    0,
                    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number'.
                    add(count)
                  )
              : map;
          } else {
            const _field = currentCollection
              .get('fields')
              .find(propIs('slug', tokenPath));
            const fieldId = _field && getId(_field);
            return fieldId
              ? // @ts-expect-error - TS18048 - 'map' is possibly 'undefined'. | TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number'.
                map.updateIn([getId(currentCollection), fieldId], 0, add(count))
              : map;
          }
        }, Map());
        return bindingsMap.mergeDeepWith(binaryAdd, bindings);
      },
      pageSettingsBindingsMap.mergeDeepWith(binaryAdd, rssItemPubDateUsages)
    );
  }, Map());
};

function getTokenOptions(
  page: PageRecordType,
  collections: CollectionsType
): EmbedTokenOption[] | null {
  const bindingContext = getPageBindingContext(page, collections);
  const tokenOptions = bindingContext
    ? getBindingContextTokenOptions(bindingContext)
    : [];
  return tokenOptions.length === 0 ? null : tokenOptions;
}

const dynamoBranchSlugPrefix = `${dynamoPageSlugPrefix}branch_`;

/**
 * Add the canonical detail-page prefix without treating prefixed user text as canonical.
 */
export const addDynamoSlugPrefix = (
  slug: string,
  {branch}: {branch: boolean}
) => `${branch ? dynamoBranchSlugPrefix : dynamoPageSlugPrefix}${slug}`;

/**
 * Convert a static slug to a dynamo detail page slug.
 * @param  {String} slug The original collection slug, e.g. "team"
 * @return {String}      A modified slug which indicates that the corresponding page is a detail page template.
 */
export const dynamoSlug = (slug: string) => {
  if (!startsWith(slug, dynamoPageSlugPrefix)) {
    return addDynamoSlugPrefix(slug, {branch: false});
  }

  return slug;
};

/**
 * Strip the prefix from a dynamo detail page slug to the raw slug.
 * @param  {String} A slug with the dynamo slug prefix, i.e. "detail_team"
 * @return {String} slug The original collection slug without the prefix, e.g. "team"
 */
export const removeDynamoSlug = (slug: string) => {
  if (startsWith(slug, dynamoPageSlugPrefix)) {
    return slug.slice(dynamoPageSlugPrefix.length);
  }
  return slug;
};

/**
 * Convert a static slug to a branch slug for a dynamo detail page.
 * @param  {String} slug The original collection slug, e.g. "team"
 * @return {String}      A modified slug which indicates that the corresponding page is a branch of a detail page template.
 */
export const dynamoBranchSlug = (slug: string) => {
  if (!startsWith(slug, dynamoPageSlugPrefix)) {
    return addDynamoSlugPrefix(slug, {branch: true});
  }

  return slug;
};

/**
 * Convert a static slug to a branch slug for a dynamo detail page.
 * @param  {String} A slug with the dynamo branch slug prefix, i.e. "detail_branch_team"
 * @return {String} slug The original collection slug without the prefix, e.g. "team"
 */
export const removeDynamoBranchSlug = (slug: string) => {
  if (startsWith(slug, dynamoBranchSlugPrefix)) {
    return slug.slice(dynamoBranchSlugPrefix.length);
  }

  return slug;
};

export const menuItemDescription = (id: string) => {
  if (id === 'save-as-draft-option') {
    return 'Drafts will not be published or indexed by search engines.';
  }
  if (id === 'staged-for-publish-option') {
    return 'Save changes. This page will publish the next time you publish the project.';
  }
  if (id === 'staged-current-locale-for-publish-option') {
    return 'Save changes for current locale only. This page will publish the next time you publish the project.';
  }
  if (id === 'save-current-locale-as-draft-option') {
    return 'This will only apply in the current locale. Drafts will not be published or indexed by search engines.';
  }
  if (id === 'create-page-as-branch-option') {
    return 'The page is added to your site when the branch is merged.';
  }
  return '';
};

export const tooltipMessage = (node: PageRecordType) => {
  if (isHomePage(node)) {
    return 'The homepage can’t be a draft.';
  }
  if (node.slug === '401') {
    return 'The password utility page can’t be a draft.';
  }
  if (node.slug === '404') {
    return 'The 404 utility page can’t be a draft.';
  }
  if (node.slug === 'checkout') {
    return 'The checkout page can’t be a draft.';
  }
  if (node.slug === 'order-confirmation') {
    return 'The order confirmation page can’t be a draft.';
  }
  if (node.slug === 'paypal-checkout') {
    return 'The PayPal checkout page can‘t be a draft.';
  }
  return '';
};

export const isDetailPageSlug = test(new RegExp('^' + dynamoPageSlugPrefix));

/**
 * NOTE: This function is not aware of the page tree, so it won't include any
 * parent folders for a detail page href. This function should only be used
 * in areas where the page tree is not available, such as when the renderer
 * renders a site that hasn't been published since collection folders were released.
 *
 * Use __DEPRECATED__getHrefToPage instead if you have the pageState.
 */
export const __DEPRECATED__getDetailPageHref = (
  pageSlug: string,
  itemSlug?: string | null
) => {
  return pageSlug && itemSlug && isDetailPageSlug(pageSlug)
    ? `/${pageSlug.replace(dynamoPageSlugPrefix, '')}/${itemSlug}`
    : '#';
};

/**
 * This function will return the full href for a given pageId and other optional params.
 *
 * @deprecated this function depends on the `PageState` Flux store. Please use `getHrefToPage` instead
 * and pass the page nodes directly.
 */
export const __DEPRECATED__getHrefToPage = ({
  pageState,
  pageId,
  itemSlug,
  currentLocale,
}: {
  pageState: PageState;
  pageId: string;
  itemSlug?: string | null | undefined;
  currentLocale?: Locale | null | undefined;
}) => {
  const pages = getPageNodes(pageState);
  return getHrefToPage({
    pages,
    pageId,
    itemSlug,
    currentLocale,
  });
};

export const getHrefToPage = ({
  pages,
  pageId,
  itemSlug,
  currentLocale,
}: {
  pages: Map<string, PageRecordType>;
  pageId: string;
  itemSlug?: string | null | undefined;
  currentLocale?: Locale | null | undefined;
}) => {
  return (
    fullSlug(pageId, pages, {currentLocale}) + (itemSlug ? '/' + itemSlug : '')
  );
};

/**
 * Returns the Page record stored in `PageState` matching `currentPageId`.
 */
export const getCurrentPage = (
  currentPageId: string,
  pageState: PageState
): PageRecordType | null | undefined => {
  return (pageState.getIn(['staticPages', currentPageId]) ||
    pageState.getIn(['dynamicPages', currentPageId])) as
    | PageRecordType
    | null
    | undefined;
};

export const getPageFullSlug = (
  pages: Map<string, PageRecordType>,
  currentPage: PageRecordType,
  currentLocale: Locale | null | undefined,
  currentCMSItem: ItemRecordType | null | undefined
) => {
  let slug: string | null = null;
  if (isDynPage(currentPage)) {
    const currentItemSlug = currentCMSItem && currentCMSItem.get('slug');
    slug = currentItemSlug || null;
  }

  let parentPageId = null;
  if (isBranchPage(currentPage)) {
    parentPageId = currentPage.parentId;
  }

  return getHrefToPage({
    pages,
    pageId: parentPageId || currentPage.id,
    itemSlug: slug,
    currentLocale,
  });
};

export const createBranchTooltip = (
  canBranchPage: boolean,
  itemType: string,
  isBranchInUse?: boolean,
  options?: {restrictionMessage?: string}
) => {
  const {t} = designerTranslation;
  if (options?.restrictionMessage) {
    return options.restrictionMessage;
  }

  if (isBranchInUse) {
    return t('designer.branches.presence.warning');
  }

  return canBranchPage
    ? `Create ${itemType.toLowerCase()} branch`
    : `To create a new branch for this page, merge the existing branch, or delete it`;
};

export const mergeBranchTooltip = ({
  canMergeBranch,
  hasPermissionToMergeApprovedBranch,
  hasPermissionToMergeBranch,
  activeReviewStatus,
  isBranchInUse,
  isBranchMergeInProgressRemotely,
  branchMergeUserName,
  restrictionMessage,
}: {
  canMergeBranch: boolean;
  hasPermissionToMergeApprovedBranch: boolean;
  hasPermissionToMergeBranch: boolean;
  activeReviewStatus: ReviewStatusType | undefined;
  isBranchInUse: boolean;
  isBranchMergeInProgressRemotely: boolean;
  branchMergeUserName?: string;
  restrictionMessage?: string;
}): string => {
  const {t} = designerTranslation;

  if (restrictionMessage) {
    return restrictionMessage;
  }

  if (isBranchInUse) {
    return t('designer.branches.presence.warning');
  }

  if (canMergeBranch) {
    return t('designer.branches.merge.enabled');
  }

  // If a user has permission to merge a branch but cannot, they must need design control.
  if (hasPermissionToMergeBranch) {
    if (isBranchMergeInProgressRemotely) {
      return t(
        'designer.branches.merge.disabled.any-branch-merge-in-progress-text',
        {
          name: branchMergeUserName || 'User',
        }
      );
    }

    return t('designer.branches.merge.disabled.design-control-text');
  }

  if (hasPermissionToMergeApprovedBranch) {
    // If the user cannot merge, but has permission to merge approved branches
    // the branch needs approval or a remote merge is in progress.
    // TODO: Add a specific message for changes_requested.
    if (
      isReviewApproved(activeReviewStatus) &&
      isBranchMergeInProgressRemotely
    ) {
      return t(
        'designer.branches.merge.disabled.any-branch-merge-in-progress-text',
        {
          name: branchMergeUserName || 'User',
        }
      );
    } else if (isReviewPending(activeReviewStatus)) {
      return t('designer.branches.merge.disabled.review-pending-text');
    } else {
      return t('designer.branches.merge.disabled.need-review-text');
    }
  }
  // If the user doesn't have permission to merge their branch or merge an approved branch,
  // their role must not allow it.
  return t('designer.branches.merge.disabled.role-permissions-text');
};

export const canBranchPage = ({
  page,
  canAccessPageBranching,
  isOptimizeMode,
}: {
  page: PageRecordType;
  canAccessPageBranching: boolean;
  isOptimizeMode: boolean;
}) =>
  canAccessPageBranching &&
  isPage(page) &&
  !isBranchPage(page) &&
  !isOptimizeMode;

export const isCommerceUtilityPage = (page: PageRecordType) =>
  page.slug === 'checkout' ||
  page.slug === 'branch_checkout' ||
  page.slug === 'order-confirmation' ||
  page.slug === 'branch_order-confirmation' ||
  page.slug === 'paypal-checkout' ||
  page.slug === 'branch_paypal-checkout';

export const sortByNameDesc = (
  pages: List<PageRecordType>
): List<PageRecordType> => pages.sortBy((page) => page.name).reverse();

type CustomCode = string | undefined;
type GetEnterpriseSnippetNameFromCode = (code: CustomCode) => CustomCode;

const getEnterpriseSnippetNameFromCode: GetEnterpriseSnippetNameFromCode =
  (() => {
    // Pre-compile snippet patterns once (ENTERPRISE_SNIPPETS is a constant)
    const compiledSnippets = Object.entries(ENTERPRISE_SNIPPETS).map(
      ([name, pattern]) => {
        const inner = pattern.replace(/^%|%$/g, '');
        const regexPattern = inner.replace(/%/g, '.*');
        return {name, regex: new RegExp(regexPattern)};
      }
    );

    // Immutable Map cache — use reassignment to fix the original bug where
    // .set() returns a new Map but was never reassigned
    let cache = Map<string, CustomCode>();
    const MAX_CACHE_SIZE = 100;

    return (code: CustomCode): CustomCode => {
      if (!code || typeof code !== 'string') return undefined;

      const cacheKey = hashCode(code)?.toString();

      if (cacheKey && cache.has(cacheKey)) {
        return cache.get(cacheKey);
      }

      // Single pass: find the first matching snippet name
      let result: CustomCode;
      for (const {name, regex} of compiledSnippets) {
        if (regex.test(code)) {
          result = name;
          break;
        }
      }

      if (cacheKey) {
        if (cache.size >= MAX_CACHE_SIZE) {
          const firstKey = cache.keys().next().value;
          if (firstKey !== undefined) cache = cache.delete(firstKey);
        }
        cache = cache.set(cacheKey, result);
      }
      return result;
    };
  })();

export const getEnterpriseSnippet = (
  head: CustomCode,
  postBody: CustomCode
) => {
  const headSnippet = getEnterpriseSnippetNameFromCode(head);
  const postBodySnippet = getEnterpriseSnippetNameFromCode(postBody);

  return [headSnippet, postBodySnippet];
};

export const intoPage = (rawPage: any): PageRecordType => {
  return PageNode(fromJS(rawPage)).merge({
    id: rawPage._id,
    name: rawPage.title,
    parentId: rawPage.parent || null,
    ...getSlugProps(rawPage),
    type: 'Page',
    siblingIndex: rawPage.sortPos || 0,
    isStandaloneBranch: rawPage.standaloneBranch || false,
    isStaticTemplate: rawPage.isStaticTemplate || false,
    description: rawPage.description || null,
    pageKind: rawPage.pageKind
      ? ImmRecord({
          kind: rawPage.pageKind.kind,
          meta: rawPage.pageKind.meta || {},
        })()
      : null,
  });
};

export const findCollectionPageIdByCollectionSlug = ({
  collectionSlug,
  pageState,
}: {
  collectionSlug: CollectionRecordType['slug'];
  pageState: PageState;
}): string => {
  if (!collectionSlug) return '';

  const detailPageSlug = dynamoSlug(collectionSlug);
  const dynPages = getDynPages(pageState);
  const targetPage = dynPages.find(
    (page: PageRecordType) => page.slug === detailPageSlug
  );

  return targetPage?.id || '';
};
